import { Flash, Column, Grid, Loader, Text, Section } from '@acast/decibel';
import T from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Container from 'shared/components/content-container';
import {
  fetchEpisodeAccessSettings as fetchEpisodeAccessSettingsAction,
  selectEpisodeAccessLoadingStatus,
  selectEpisodeAccessSettings,
  updateEpisodeAccessSettings as updateEpisodeAccessSettingsAction,
} from '../../../../ducks/access';
import { Episode } from '../../../../resources';
import resourceProp from '../../../../shared/helpers/resource-prop';
import AudioCard from './audio-card';
import * as AudioContext from './audio-context';
import LockedEpisodeCard from './locked-episode-card';

const LoaderWrapper = styled.div`
  position: absolute;
  z-index: 100;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
`;

const AccessSettingForm = props => {
  const {
    fetchEpisodeAccessSettings,
    updateEpisodeAccessSettings,
    episode,
    loading,
    settings,
  } = props;
  const { id: episodeId, show: showId } = episode.data;

  React.useEffect(() => {
    fetchEpisodeAccessSettings(showId, episodeId);
  }, [showId, episodeId, fetchEpisodeAccessSettings]);

  const updateSettings = React.useCallback(
    data => {
      updateEpisodeAccessSettings({ episodeId, data });
    },
    [episodeId, updateEpisodeAccessSettings]
  );

  const handleLockChange = React.useCallback(
    value => {
      updateSettings({ locked: value });
    },
    [updateSettings]
  );

  const handlePrivateIntroChange = React.useCallback(
    value => {
      updateSettings({ privateIntro: value });
    },
    [updateSettings]
  );

  const handlePrivateOutroChange = React.useCallback(
    value => {
      updateSettings({ privateOutro: value });
    },
    [updateSettings]
  );

  const handlePublicIntroChange = React.useCallback(
    value => {
      updateSettings({ publicIntro: value });
    },
    [updateSettings]
  );

  const handlePublicOutroChange = React.useCallback(
    value => {
      updateSettings({ publicOutro: value });
    },
    [updateSettings]
  );

  return (
    <Container>
      <AudioContext.Provider>
        <Flash intent="warning" mb={6}>
          <strong>NOTE:</strong> Closing or reloading this page will cause changes to Access
          settings to be lost. Save pending changes using the Publish Settings tab.
        </Flash>
        <Section>
          <Text variant="h3">Access Settings</Text>
          <Section>
            <Grid>
              <Column width="50%">
                <LockedEpisodeCard
                  disabled={loading}
                  locked={settings.locked}
                  handleChange={handleLockChange}
                />
              </Column>
            </Grid>
          </Section>

          <Section>
            <Text
              variant="h4"
              subtitle="Upload dynamic audio openings and endings to your public and private episodes."
            >
              Dynamic Audio
            </Text>
            <Grid spacingDirections={['horizontal', 'vertical']}>
              <Column width="100%">
                <AudioCard
                  disabled={loading}
                  src={settings.privateOutro}
                  episodeId={episodeId}
                  showId={showId}
                  onChangeAudioFile={handlePrivateOutroChange}
                  type="privateOutro"
                />
              </Column>
              <Column width="50%">
                <AudioCard
                  disabled={loading || settings.locked}
                  src={settings.publicIntro}
                  episodeId={episodeId}
                  showId={showId}
                  onChangeAudioFile={handlePublicIntroChange}
                  type="publicIntro"
                />
              </Column>
              <Column width="50%">
                <AudioCard
                  disabled={loading || settings.locked}
                  src={settings.publicOutro}
                  episodeId={episodeId}
                  showId={showId}
                  onChangeAudioFile={handlePublicOutroChange}
                  type="publicOutro"
                />
              </Column>
              <Column width="50%">
                <AudioCard
                  disabled={loading}
                  src={settings.privateIntro}
                  episodeId={episodeId}
                  showId={showId}
                  onChangeAudioFile={handlePrivateIntroChange}
                  type="privateIntro"
                />
              </Column>
            </Grid>
          </Section>
        </Section>
      </AudioContext.Provider>
      {loading && (
        <LoaderWrapper>
          <Loader width={26} />
        </LoaderWrapper>
      )}
    </Container>
  );
};

AccessSettingForm.propTypes = {
  episode: resourceProp(Episode),
  fetchEpisodeAccessSettings: T.func.isRequired,
  loading: T.bool.isRequired,
  updateEpisodeAccessSettings: T.func.isRequired,
  settings: T.shape({
    locked: T.bool,
    privateIntro: T.string,
    privateOutro: T.string,
    publicIntro: T.string,
    publicOutro: T.string,
  }),
};

AccessSettingForm.defaultProps = {
  settings: {},
};

export default connect(
  (state, { episode }) => ({
    settings: selectEpisodeAccessSettings(state, episode.data.id),
    loading: selectEpisodeAccessLoadingStatus(state, episode.data.id),
  }),
  {
    fetchEpisodeAccessSettings: fetchEpisodeAccessSettingsAction,
    updateEpisodeAccessSettings: updateEpisodeAccessSettingsAction,
  }
)(AccessSettingForm);
